import { useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { showToast } from "@/components/ui";
import {
  useCancelTaxonomyApprovalPipelineJobMutation,
  useMarkTaxonomyApprovalPipelineJobFailedMutation,
  useResumeTaxonomyApprovalPipelineQueueMutation,
  useRetryTaxonomyApprovalPipelineJobMutation,
  useTaxonomyApprovalPipelineJobQuery,
  useTaxonomyApprovalPipelineJobsQuery,
  type TaxonomyApprovalPipelineJobQuery,
  type TaxonomyApprovalPipelineJobsQuery,
} from "@/graphql/generated";
import { useTaxonomyApprovalPipelineSubscription } from "@/hooks/useTaxonomyApprovalPipelineSubscription";

export interface ApprovalPipelineJobAction {
  key: string;
  label: string;
  destructive?: boolean;
}

export interface ApprovalPipelineJobRecord {
  id: string;
  syncJobId: string;
  pipelineId?: string | null;
  mappingSessionId: string;
  status: string;
  taxonomyStatus?: string | null;
  retryable?: boolean | null;
  terminal: boolean;
  resourceLockKey?: string | null;
  message?: string | null;
  error?: string | null;
  occurredAt: string;
  updatedAt?: string | null;
  approvedBy?: string | null;
  notes?: string | null;
  fieldDecisionCount: number;
  canonicalValueDecisionCount: number;
  marketplaceSpecificValueDecisionCount: number;
  payload?: unknown;
  actions: ApprovalPipelineJobAction[];
}

export interface ApprovalPipelineJobDetailLedgerEntry {
  id: string;
  syncJobId?: string | null;
  status: string;
  eventType?: string | null;
  error?: string | null;
  retryable?: boolean | null;
  payload?: unknown;
  createdAt: string;
}

export interface ApprovalPipelineJobDetailRecord {
  job: ApprovalPipelineJobRecord;
  ledger: ApprovalPipelineJobDetailLedgerEntry[];
}

type PendingActionType = "RETRY" | "CANCEL" | "MARK_FAILED" | "RESUME_QUEUE";

export interface ApprovalPipelineActionModalState {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  variant: "danger" | "primary";
  reason: string;
  setReason: (value: string) => void;
  close: () => void;
  confirm: () => Promise<void>;
  busy: boolean;
}

export interface UseApprovalPipelineJobsResult {
  jobs: ApprovalPipelineJobRecord[];
  loading: boolean;
  refreshing: boolean;
  errorMessage: string | null;
  search: string;
  setSearch: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  filteredJobs: ApprovalPipelineJobRecord[];
  selectedSyncJobId: string | null;
  setSelectedSyncJobId: (value: string | null) => void;
  selectedJobDetail: ApprovalPipelineJobDetailRecord | null;
  selectedJobLoading: boolean;
  refresh: () => Promise<void>;
  requestAction: (
    action: ApprovalPipelineJobAction["key"],
    job: ApprovalPipelineJobRecord,
  ) => void;
  requestResumeQueue: (mappingSessionId: string) => void;
  actionModal: ApprovalPipelineActionModalState;
}

type JobListItem =
  TaxonomyApprovalPipelineJobsQuery["taxonomyApprovalPipelineJobs"]["items"][number];
type JobDetailPayload = NonNullable<
  TaxonomyApprovalPipelineJobQuery["taxonomyApprovalPipelineJob"]
>;

const TERMINAL_STATUSES = new Set([
  "SUCCESS",
  "FAILED",
  "CANCELLED",
  "SKIPPED",
]);

function toTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status.trim().toUpperCase());
}

function mapAvailableAction(action: string): ApprovalPipelineJobAction | null {
  switch (action) {
    case "RETRY":
      return { key: "RETRY", label: "Retry" };
    case "CANCEL":
      return { key: "CANCEL", label: "Cancel", destructive: true };
    case "MARK_FAILED":
      return {
        key: "MARK_FAILED",
        label: "Mark Failed",
        destructive: true,
      };
    default:
      return null;
  }
}

function mapJob(job: JobListItem): ApprovalPipelineJobRecord {
  const availableActions = job.availableActions
    .map(mapAvailableAction)
    .filter((value): value is ApprovalPipelineJobAction => Boolean(value));

  return {
    id: job.syncJobId,
    syncJobId: job.syncJobId,
    pipelineId: job.pipelineId ?? null,
    mappingSessionId: job.mappingSessionId,
    status: job.status,
    taxonomyStatus: job.taxonomyStatus ?? null,
    retryable: job.retryable,
    terminal: toTerminal(job.status),
    resourceLockKey: job.resourceLockKey ?? null,
    message: job.message ?? null,
    error: job.error ?? null,
    occurredAt:
      job.lastLedgerAt ??
      job.failedAt ??
      job.completedAt ??
      job.dispatchedAt ??
      job.startedAt ??
      job.queuedAt,
    updatedAt: job.updatedAt,
    approvedBy: job.approvedBy ?? null,
    notes: job.notes ?? null,
    fieldDecisionCount: job.fieldDecisionCount,
    canonicalValueDecisionCount: job.canonicalValueDecisionCount,
    marketplaceSpecificValueDecisionCount:
      job.marketplaceSpecificValueDecisionCount,
    payload: undefined,
    actions: availableActions,
  };
}

function mapDetail(detail: JobDetailPayload): ApprovalPipelineJobDetailRecord {
  const mappedJob = mapJob(detail.job);
  return {
    job: {
      ...mappedJob,
      payload: detail.job.payload ?? null,
    },
    ledger: detail.ledger.map((entry) => ({
      id: entry.id,
      syncJobId: entry.syncJobId ?? null,
      status: entry.status,
      eventType: entry.eventType ?? null,
      error: entry.error ?? null,
      retryable: entry.retryable ?? null,
      payload: entry.payload ?? null,
      createdAt: entry.createdAt,
    })),
  };
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  return "Something went wrong while loading approval jobs.";
}

export function useApprovalPipelineJobs(): UseApprovalPipelineJobsResult {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedSyncJobId, setSelectedSyncJobId] = useState<string | null>(
    null,
  );
  const [pendingAction, setPendingAction] = useState<{
    type: PendingActionType;
    job?: ApprovalPipelineJobRecord;
    mappingSessionId?: string;
  } | null>(null);
  const [actionReason, setActionReason] = useState("");

  const jobsQuery = useTaxonomyApprovalPipelineJobsQuery(
    { input: { limit: 100 } },
    {
      refetchOnWindowFocus: false,
    },
  );

  const jobs = useMemo(
    () =>
      (jobsQuery.data?.taxonomyApprovalPipelineJobs.items ?? []).map(mapJob),
    [jobsQuery.data],
  );

  useEffect(() => {
    if (jobs.length === 0) {
      if (selectedSyncJobId) setSelectedSyncJobId(null);
      return;
    }

    const selectedStillExists = selectedSyncJobId
      ? jobs.some((job) => job.syncJobId === selectedSyncJobId)
      : false;

    if (!selectedStillExists) {
      setSelectedSyncJobId(jobs[0]?.syncJobId ?? null);
    }
  }, [jobs, selectedSyncJobId]);

  const detailQuery = useTaxonomyApprovalPipelineJobQuery(
    { syncJobId: selectedSyncJobId ?? "" },
    {
      enabled: Boolean(selectedSyncJobId),
      refetchOnWindowFocus: false,
    },
  );

  useTaxonomyApprovalPipelineSubscription({
    syncJobId: selectedSyncJobId,
    enabled: Boolean(selectedSyncJobId),
    onUpdate: () => {
      queryClient.invalidateQueries({
        queryKey: ["TaxonomyApprovalPipelineJobs"],
      });
      if (selectedSyncJobId) {
        queryClient.invalidateQueries({
          queryKey: [
            "TaxonomyApprovalPipelineJob",
            { syncJobId: selectedSyncJobId },
          ],
        });
      }
    },
  });

  const cancelMutation = useCancelTaxonomyApprovalPipelineJobMutation();
  const retryMutation = useRetryTaxonomyApprovalPipelineJobMutation();
  const markFailedMutation = useMarkTaxonomyApprovalPipelineJobFailedMutation();
  const resumeQueueMutation = useResumeTaxonomyApprovalPipelineQueueMutation();

  const filteredJobs = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return jobs.filter((job) => {
      const normalizedStatus = job.status.trim().toUpperCase();
      const matchesStatus =
        statusFilter === "ALL" ||
        normalizedStatus === statusFilter.toUpperCase();
      if (!matchesStatus) return false;

      if (!normalizedSearch) return true;

      return [
        job.syncJobId,
        job.pipelineId,
        job.mappingSessionId,
        job.resourceLockKey,
        job.approvedBy,
        job.message,
        job.error,
        job.notes,
      ]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [jobs, search, statusFilter]);

  const selectedJobDetail = useMemo(() => {
    const detail = detailQuery.data?.taxonomyApprovalPipelineJob;
    return detail ? mapDetail(detail) : null;
  }, [detailQuery.data]);

  const refresh = async () => {
    await Promise.all([jobsQuery.refetch(), detailQuery.refetch()]);
  };

  const invalidateJobs = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["TaxonomyApprovalPipelineJobs"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["TaxonomyApprovalPipelineJob"],
      }),
    ]);
  };

  const closeActionModal = () => {
    setPendingAction(null);
    setActionReason("");
  };

  const requestAction = (
    action: ApprovalPipelineJobAction["key"],
    job: ApprovalPipelineJobRecord,
  ) => {
    if (action !== "RETRY" && action !== "MARK_FAILED" && action !== "CANCEL")
      return;
    setPendingAction({ type: action, job });
    setActionReason("");
  };

  const requestResumeQueue = (mappingSessionId: string) => {
    setPendingAction({ type: "RESUME_QUEUE", mappingSessionId });
    setActionReason("");
  };

  const confirmAction = async () => {
    if (!pendingAction) return;

    try {
      if (pendingAction.type === "CANCEL" && pendingAction.job) {
        const response = await cancelMutation.mutateAsync({
          input: {
            syncJobId: pendingAction.job.syncJobId,
            auditNote: actionReason.trim(),
          },
        });
        showToast.success(
          "Job cancelled",
          response.cancelTaxonomyApprovalPipelineJob.message,
        );
      } else if (pendingAction.type === "RETRY" && pendingAction.job) {
        const response = await retryMutation.mutateAsync({
          input: {
            syncJobId: pendingAction.job.syncJobId,
            auditNote: actionReason.trim(),
          },
        });
        showToast.success(
          "Retry queued",
          response.retryTaxonomyApprovalPipelineJob.message,
        );
      } else if (pendingAction.type === "MARK_FAILED" && pendingAction.job) {
        const response = await markFailedMutation.mutateAsync({
          input: {
            syncJobId: pendingAction.job.syncJobId,
            auditNote: actionReason.trim(),
          },
        });
        showToast.success(
          "Job marked failed",
          response.markTaxonomyApprovalPipelineJobFailed.message,
        );
      } else if (
        pendingAction.type === "RESUME_QUEUE" &&
        pendingAction.mappingSessionId
      ) {
        const response = await resumeQueueMutation.mutateAsync({
          input: {
            mappingSessionId: pendingAction.mappingSessionId,
            auditNote: actionReason.trim(),
          },
        });
        showToast.success(
          "Queue resume processed",
          response.resumeTaxonomyApprovalPipelineQueue.message,
        );
      }

      closeActionModal();
      await invalidateJobs();
    } catch (error) {
      showToast.error("Action failed", getErrorMessage(error));
    }
  };

  const actionBusy =
    cancelMutation.isPending ||
    retryMutation.isPending ||
    markFailedMutation.isPending ||
    resumeQueueMutation.isPending;

  const actionModalTitle =
    pendingAction?.type === "CANCEL"
      ? "Cancel queued job"
      : pendingAction?.type === "MARK_FAILED"
        ? "Mark job as failed"
        : pendingAction?.type === "RESUME_QUEUE"
          ? "Resume queued session work"
          : "Retry approval job";

  const actionModalDescription =
    pendingAction?.type === "CANCEL"
      ? "Use this for queued approval jobs you do not want to dispatch. The job will move to cancelled and can still be retried later if needed."
      : pendingAction?.type === "MARK_FAILED"
        ? "Use this when a running or dispatching job is stale and is blocking the session queue. The next queued job can move forward after recovery."
        : pendingAction?.type === "RESUME_QUEUE"
          ? "Use this after a stale blocker is cleared and you want the next queued approval job for this mapping session to start."
          : "Retry the selected approval job and re-enqueue it through the normal pipeline.";

  return {
    jobs,
    loading: jobsQuery.isLoading,
    refreshing: jobsQuery.isFetching || detailQuery.isFetching,
    errorMessage:
      (jobsQuery.error && getErrorMessage(jobsQuery.error)) ||
      (detailQuery.error && getErrorMessage(detailQuery.error)) ||
      null,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    filteredJobs,
    selectedSyncJobId,
    setSelectedSyncJobId,
    selectedJobDetail,
    selectedJobLoading: detailQuery.isLoading,
    refresh,
    requestAction,
    requestResumeQueue,
    actionModal: {
      open: Boolean(pendingAction),
      title: actionModalTitle,
      description: actionModalDescription,
      confirmLabel:
        pendingAction?.type === "CANCEL"
          ? "Cancel Job"
          : pendingAction?.type === "MARK_FAILED"
            ? "Mark Failed"
            : pendingAction?.type === "RESUME_QUEUE"
              ? "Resume Queue"
              : "Retry Job",
      variant:
        pendingAction?.type === "MARK_FAILED" ||
        pendingAction?.type === "CANCEL"
          ? "danger"
          : "primary",
      reason: actionReason,
      setReason: setActionReason,
      close: closeActionModal,
      confirm: confirmAction,
      busy: actionBusy,
    },
  };
}
